import {
  Fragment,
  lazy,
  Suspense,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
  type Ref,
} from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm, useFormState, useWatch, type Control } from "react-hook-form";
import { CheckIcon, InfoIcon, PlusIcon } from "lucide-react";
import { z } from "zod";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CampoConAsistencia } from "@/components/informes/CampoConAsistencia";
import { FilaDeRol, rolVacio } from "@/components/informes/FilaDeRol";
import { ImagenDeLaPropiedad, ImagenPendiente } from "@/components/informes/ImagenDeLaPropiedad";
import { ModalDeCambioDeEncargado } from "@/components/informes/ModalDeCambioDeEncargado";
import { BarraDeGuardado } from "@/components/informes/BarraDeGuardado";
import { IndiceDeLaPestana } from "@/components/informes/IndiceDeLaPestana";
import { SelectorDeContenidos } from "@/components/informes/SelectorDeContenidos";
import { SegmentoDeUnidad } from "@/components/informes/SegmentoDeUnidad";
import { SegmentoDeRetasacion } from "@/components/informes/SegmentoDeRetasacion";
import { TarjetaDeSeccion } from "@/components/informes/TarjetaDeSeccion";
import { useContenidosDeLaPlantilla } from "@/hooks/usePlantillas";
import { CAMPOS_CON_ASISTENCIA } from "@/lib/ia";
import {
  ROLES_RESPONSABLE,
  type CatalogosDelInforme,
  type CrearInformeInput,
  type Informe,
  type PropuestaDeTasacion,
  type RolResponsable,
} from "@/lib/informes";

import {
  itemsParaEnviar,
  normalizarSeleccion,
  seleccionCambiada,
  seleccionInicial,
} from "@/lib/seleccionDeContenidos";
import { cn } from "@/lib/utils";
import type { CampoDeTerreno } from "@/lib/terreno";

import { VisorDeFotos } from "./FotosDeTerreno";
import { PanelDeTerrenoDeLaDescripcion } from "./PanelDeTerrenoDeLaDescripcion";
import { PortadaDelInforme } from "./PortadaDeTerreno";
import { PuntoDeAccesoDeTerreno } from "./PuntoDeAccesoDeTerreno";
import { LineaDeRolDeTerreno, RolesAgregadosEnTerreno } from "./RolesDeTerreno";
import { rolesDeTerreno } from "./rolesTerreno";
import { RedactarDesdeTerreno } from "./RedactarDesdeTerreno";
import { fotosDelVisor } from "./registroDeTerreno";
import {
  coordenadaDelPunto,
  distanciaEnMetros,
  mismaCoordenada,
  puedeRedactarDesdeTerreno,
  textoDeDistancia,
  vistaDeTerreno,
  type EstadoDelTerreno,
} from "./terrenoDeAntecedentes";
import {
  contarCambios,
  idDeLaSeccion,
  pendientesDeLaPestana,
  seccionesCompletas,
  type ClaveDeSeccion,
  type EntradaDeAntecedentes,
} from "./antecedentesGenerales";
import { NotasDeLaPestana } from "./NotasDeLaPestana";
import {
  formatearSuperficie,
  parsearSuperficie,
  sumarSuperficies,
} from "./superficies";

/* Perfiles que pueden ocupar un rol de revisor. Se decide por CÓDIGO y no por
   nombre porque el nombre del perfil es editable (RF-C3) y no puede ser la
   identidad sobre la que se filtra un select.

   El Super Admin entra junto al Revisor: RF-G3 ya lo habilita a visar
   CUALQUIER cotización, así que dejarlo fuera del select producía la
   contradicción de que podía visar un informe pero no quedar designado como su
   revisor. El API nunca exigió el perfil —`StoreInformeRequest` solo pide que
   el usuario tenga acceso—, de modo que la restricción vivía únicamente acá. */
const CODIGOS_REVISOR = ["RV", "SA"];

/* Los números viajan como string porque eso es lo que entrega un <input>; la
   conversión ocurre al enviar, con `parsearSuperficie` (que entiende la coma
   decimal). Validar sobre string y no sobre `z.coerce.number()` es lo que
   permite aceptar "12,5". */
const superficieOpcional = z
  .string()
  .trim()
  .refine(
    (valor) => valor === "" || (parsearSuperficie(valor) ?? -1) >= 0,
    "Ingresa un número válido.",
  );

/**
 * El mapa se carga PEREZOSO, como todo lo que arrastra Leaflet: son ~158 kB de
 * JS más ~16 kB de CSS, y el formulario del informe se abre muchas más veces de
 * las que alguien marca un predio.
 */
const SelectorDeCoordenada = lazy(() =>
  import("@/components/mapa/SelectorDeCoordenada").then((modulo) => ({
    default: modulo.SelectorDeCoordenada,
  })),
);

/**
 * Dónde se abre el mapa cuando el informe todavía no tiene predio marcado.
 *
 * Es un PUNTO DE PARTIDA para navegar, no una sugerencia: el valle central,
 * con zoom bajo, que es de donde viene la mayoría de los encargos. No se
 * deriva de la comuna del informe porque el maestro de comunas guarda el
 * decreto —código, nombre y región—, no coordenadas.
 */
const VISTA_INICIAL: [number, number] = [-35.5, -71.5];
const ZOOM_INICIAL = 6;

const rolSchema = z.object({
  /* CUÁL de los roles que ya existen es este. Ausente en uno recién agregado
     en la pantalla, y esa ausencia significa "créalo".

     No se dibuja en ninguna parte y no se edita: viaja para que el servidor
     RECONCILIE en vez de borrar y recrear. Sin él, guardar esta pestaña se
     llevaría la imagen del límite y la descripción de deslindes de todos los
     roles —el trabajo entero de "Descripción de la propiedad"— sin que nada
     fallara. El ALTA no lo manda (los roles nacen sin id) y el servidor lo
     rechaza ahí con un 422. */
  id: z.number().optional(),
  rol_sii: z.string().trim().min(1, "El rol es obligatorio."),
  /* El código CUT de la comuna elegida del maestro. "" = todavía sin elegir,
     que es válido por la misma razón que el resto del formulario admite
     borradores. Viaja como `null`. */
  comuna_codigo: z.string().trim(),
  /* El nombre que el informe tiene CONGELADO. Ya NO se edita: cuando hay
     código, el backend congela el del maestro e ignora esto. Sigue acá para
     los informes anteriores al maestro, que tienen texto y ningún código. */
  comuna: z.string().trim(),
  /* LAS DOS SUPERFICIES SON OPCIONALES Y SU VACÍO NO SIGNIFICA LO MISMO:
     terreno vacío se imprime "—" (no corresponde, p. ej. Bien Común) y
     construida vacía se imprime "s/i". */
  superficie: superficieOpcional,
  superficie_construida: superficieOpcional,
});

const informeSchema = z.object({
  /* El número del informe en el ERP de Consultek. OBLIGATORIO: es la clave con
     la que este informe se cruza contra ese sistema, así que uno sin número
     nace desconectado y nadie lo nota hasta semanas después.
     Sin regla de formato: el formato lo decide el ERP, y cualquier patrón que
     inventáramos acá rechazaría un número que el ERP ya emitió. Su unicidad la
     valida el backend, que es el único que puede verla. */
  numero_informe: z
    .string()
    .trim()
    .min(1, "El número del informe es obligatorio: es con lo que se cruza contra el ERP.")
    .max(50, "El número no puede superar los 50 caracteres."),
  cliente_nombre: z.string().trim().min(1, "El nombre del cliente es obligatorio."),
  /* El cargo de quien recibe la carta ("ENCARG. PROYECTOS Y TASACIONES BCI").
     Opcional: dos de las cinco cotizaciones reales no lo llevan porque el
     cliente es un particular, y ahí el documento OMITE el renglón. */
  cargo_cliente: z.string().trim(),
  propiedad_nombre: z.string().trim().min(1, "El nombre de la propiedad es obligatorio."),
  telefono_contacto: z.string().trim().min(1, "El teléfono del cliente es obligatorio."),
  /* Obligatorio como el teléfono: es a donde sale la cotización aprobada
     (RF-G3). Opcional, el envío al cliente fallaría en silencio justo en el
     paso que cierra el trato. `.email()` valida la forma, que es lo único
     verificable sin mandar un correo de confirmación a un tercero. */
  email_contacto: z
    .string()
    .trim()
    .min(1, "El correo del cliente es obligatorio: es a donde se envía la cotización.")
    .email("Escribe un correo válido."),
  solicitante_nombre: z.string().trim().min(1, "El nombre del solicitante es obligatorio."),
  /* LA COORDENADA DEL PREDIO, como UN OBJETO NULLABLE y no como dos campos
     sueltos. Media coordenada no ubica nada —no es un punto a medio camino,
     es un dato roto— y con dos entradas independientes eso se puede escribir;
     así es imposible de representar. El servidor tiene su propia regla
     cruzada, que guarda contra otros clientes: esta hace que desde el
     formulario no haya nada de qué guardarse. */
  coordenada: z
    .object({ latitud: z.number(), longitud: z.number() })
    .nullable(),
  direccion: z.string().trim(),
  descripcion_ubicacion: z.string().trim(),
  descripcion: z.string().trim(),
  /* El formato de salida reemplazó al uso de la tasación. Texto por ser el
     valor de un select; debe ser uno de los que admite la plantilla elegida
     (el servidor lo vuelve a verificar). */
  formato_salida_id: z.string().min(1, "Selecciona el formato de salida."),
  /* Tres estados, como el select de RF-E3: "" = Sin verificar, que es distinto
     de "no". Se traduce a `null | true | false` al enviar. */
  es_retasacion: z.enum(["", "si", "no"]),
  /* La unidad del encabezado de la columna Superficie de la tabla de roles y
     de su TOTAL. Vivía por bloque; en el árbol plano es del INFORME entero —
     relocada, no reinventada: mismos dos valores, mismo control. */
  unidad_superficie: z.enum(["ha", "m2"]),
  /* DOS NIVELES, no tres: el informe tiene ROLES directos, cada uno con su
     comuna y su superficie. Reversión deliberada del nivel "propiedad", que
     nació de la 22160 —en realidad cinco tasaciones distintas metidas en un
     documento— y no de un caso general: la 22192 (una propiedad, varios
     roles, una comuna) ya se representaba bien sin él. */
  roles: z.array(rolSchema).min(1, "Agrega al menos un rol."),
  /* Aclara lo que la imagen satelital NO muestra —por ejemplo, que los roles
     caen en dos comunas y la captura solo encuadra una—. Opcional: la mayoría
     de los informes no la necesitan. */
  nota_imagen: z.string().trim(),
  /* El TIPO DE TASACIÓN ES la plantilla (Rodgers, 08-10-2026): una sola
     elección que además manda qué contenidos existen y cuáles son obligatorios;
     `items` son los ids del maestro que ESTE informe lleva. Texto y no número
     en `plantilla_id` porque es el valor de un select (el vacío es "sin
     elegir"). */
  plantilla_id: z.string().min(1, "Selecciona el tipo de tasación."),
  items: z.array(z.number()).min(1, "Selecciona al menos un contenido."),
  /* Solo la edición los muestra; en el alta viajan con su valor inicial y el
     backend los ignora (no están en `StoreInformeRequest`). */
  descripcion_ubicacion_validada: z.boolean(),
  descripcion_validada: z.boolean(),
  notas_observaciones: z.string(),
  responsables: z.object({
    encargado_terreno: z.string().min(1, "Selecciona un responsable."),
    ejecutivo: z.string().min(1, "Selecciona un responsable."),
    revisor_cotizacion: z.string().min(1, "Selecciona un responsable."),
    revisor_informe: z.string().min(1, "Selecciona un responsable."),
  }),
});

export type InformeValues = z.infer<typeof informeSchema>;

/**
 * Un rol repetido es la misma parcela ante el SII, y el backend lo rechaza con
 * 422 — pero avisarlo acá evita el viaje.
 *
 * LA UNICIDAD ES DEL PAR (comuna, rol) y NO DEL ROL SOLO: el propio esquema de
 * `informe_roles` lo dice ("rol + comuna es la unidad que el SII reconoce",
 * `create_informe_roles_table.php:13`), y el caso real que motivó esta vuelta
 * al modelo plano es una sucesión cuyos roles caen en comunas DISTINTAS. Una
 * unicidad global rechazaría ese informe legítimo.
 *
 * `comuna_codigo ?? ""` normaliza el `null` a string vacío ANTES de comparar:
 * es lo que hace que el front sea exactamente tan estricto como el índice
 * parcial de la base (que sí trata dos comuna-less como distintas por
 * defecto) y nunca más laxo — dos roles con el mismo número y SIN comuna
 * elegida todavía tienen que seguir chocando acá.
 */
const schemaConRolesUnicos = informeSchema.superRefine((valores, ctx) => {
  const vistos = new Set<string>();

  valores.roles.forEach((rol, indice) => {
    const rolClave = rol.rol_sii.trim().toLowerCase();
    if (rolClave === "") return;

    const clave = `${rol.comuna_codigo}::${rolClave}`;

    if (vistos.has(clave)) {
      ctx.addIssue({
        code: "custom",
        path: ["roles", indice, "rol_sii"],
        message: "Ese rol ya está agregado en este informe.",
      });
    } else {
      vistos.add(clave);
    }
  });
});

const VALORES_INICIALES: InformeValues = {
  numero_informe: "",
  cliente_nombre: "",
  cargo_cliente: "",
  propiedad_nombre: "",
  telefono_contacto: "",
  email_contacto: "",
  solicitante_nombre: "",
  coordenada: null,
  direccion: "",
  descripcion_ubicacion: "",
  descripcion: "",
  formato_salida_id: "",
  es_retasacion: "",
  unidad_superficie: "ha",
  roles: [rolVacio()],
  nota_imagen: "",
  plantilla_id: "",
  items: [],
  descripcion_ubicacion_validada: false,
  descripcion_validada: false,
  notas_observaciones: "",
  responsables: {
    encargado_terreno: "",
    ejecutivo: "",
    revisor_cotizacion: "",
    revisor_informe: "",
  },
};

/** Campos que el 422 del backend puede señalar y el formulario sabe resaltar. */
export type CampoDeInforme =
  | keyof InformeValues
  | `roles.${number}.${"rol_sii" | "comuna_codigo" | "superficie" | "superficie_construida"}`;

/** El error de un ítem concreto (`items.3`) se muestra sobre la lista entera. */
function campoDelFormulario(campo: string): CampoDeInforme {
  return (/^items\.\d+$/.test(campo) ? "items" : campo) as CampoDeInforme;
}

/**
 * Lo que la pantalla puede pedirle al formulario desde afuera.
 *
 * Existe por el aviso de cambios sin guardar: "Guardar y salir" se decide en un
 * diálogo que vive FUERA del `<form>`, así que necesita disparar el envío —con
 * su validación— sin duplicar el `handleSubmit` de react-hook-form.
 */
export interface InformeFormularioHandle {
  /**
   * Dispara el envío validado, igual que apretar el botón de guardar.
   * `alNoValidar` corre si la validación frenó el envío: quien pidió
   * "guardar y salir" necesita enterarse de que no hubo guardado, o se quedaría
   * esperando un éxito que nunca llega.
   */
  enviar: (alNoValidar?: () => void) => void;
  /**
   * Toma lo guardado como la nueva base, de modo que el formulario deje de
   * considerarse sucio. Lo llama la pantalla cuando el backend confirmó.
   *
   * Sin argumento se conserva el comportamiento viejo, que sirve para las
   * pestañas donde nada nace en el servidor.
   */
  marcarGuardado: (valores?: InformeValues) => void;
  /**
   * Vuelca sobre el formulario lo que la IA leyó de una tasación anterior.
   *
   * SOLO PISA LO QUE LA PROPUESTA TRAE. Un campo que el documento no tenía llega
   * en `null` y acá se ignora: si lo escribiera igual, importar borraría lo que
   * la persona ya había tecleado, que es exactamente lo contrario de una ayuda
   * de carga. Lo mismo con los roles — sin roles leídos, los que están se
   * quedan.
   *
   * Deja el formulario SUCIO a propósito: precargar es un cambio sin guardar
   * como cualquier otro, y la guardia de salida tiene que avisar si alguien se
   * va después de importar.
   */
  precargar: (propuesta: PropuestaDeTasacion) => void;
  /**
   * Marca "¿La propiedad cuenta con tasación anterior?" en "Sí".
   *
   * Lo llama la pantalla al retener una tasación importada: el documento
   * subido ES la evidencia, así que dejar "Sin verificar" obligaría a declarar
   * a mano algo que la pantalla ya sabe. Va aparte de `precargar` porque vale
   * aunque la IA no haya podido leer el archivo.
   */
  marcarTasacionAnterior: () => void;
}

interface InformeFormularioProps {
  catalogos: CatalogosDelInforme;
  enviando: boolean;
  /**
   * Frena el envío sin decir que se está enviando.
   *
   * Existe aparte de `enviando` porque son dos esperas distintas y confundirlas
   * miente: `enviando` pinta el spinner DENTRO del botón, que significa "se
   * está creando el informe". Lo usa la lectura de una tasación anterior, que
   * bloquea por otro motivo — crear el informe a mitad de la lectura descartaría
   * en silencio justo los datos que la persona está esperando—, y ese motivo ya
   * lo explica el bloque del importador, arriba.
   */
  bloqueado?: boolean;
  ref?: Ref<InformeFormularioHandle>;
  /**
   * Avisa si hay ediciones sin guardar. La pantalla lo usa para decidir si
   * interrumpe una salida; el formulario no sabe nada de navegación.
   */
  onCambios?: (hayCambios: boolean) => void;
  /** Ficha ya cargada: la edición precarga el formulario con ella. */
  valoresIniciales?: InformeValues;
  /**
   * Id del informe, cuando ya existe. Solo lo usa la asistencia de redacción,
   * para poder leer después el uso de IA por informe. En la creación no hay
   * ninguno todavía, y eso es válido: la corrida igual ocurrió y igual costó.
   */
  informeId?: number;
  /**
   * La edición suma las dos casillas "Información validada" y las notas al
   * pie; el alta no las muestra porque el backend no las acepta todavía.
   */
  modo?: "crear" | "editar";
  /**
   * La ficha completa, cuando el informe ya existe. La necesita la zona de
   * imagen: en la edición sube el archivo al momento, y para eso tiene que
   * saber si ya hay una guardada.
   */
  informe?: Informe;
  /**
   * La imagen elegida en el ALTA, que todavía no se puede subir porque el
   * informe no tiene id. La retiene la pantalla —es quien la va a mandar
   * cuando el alta le devuelva el id— y el formulario solo la muestra.
   */
  imagenPendiente?: File | null;
  onImagenPendiente?: (archivo: File | null) => void;
  /**
   * El bloque de "Importar una tasación anterior", que se dibuja arriba de todo.
   *
   * LO PASA SOLO LA PANTALLA DE CREACIÓN, y esa es la regla: es una ayuda de
   * CARGA, sirve mientras el formulario está vacío. La edición usa este mismo
   * componente y no lo pasa, porque ahí los datos ya están cargados y revisados
   * — ofrecerlo sería ofrecer pisarlos. Después del alta, lo que queda es
   * adjuntar desde la pestaña de antecedentes históricos, que es registro.
   *
   * Llega ya construido en vez de armarse acá para que el formulario siga sin
   * saber de la API: quien llama es quien puede leer el PDF y precargar.
   */
  importador?: ReactNode;
  /**
   * Lo que va ARRIBA de la primera tarjeta, solo en la edición. Hoy son los
   * datos de facturación del cliente: llegan ya construidos por el mismo
   * motivo que `importador`, para que el formulario siga sin saber de la API.
   */
  encabezado?: ReactNode;
  /**
   * Lo que el encargado mandó desde la App de terreno, solo en la edición.
   *
   * Es OPCIONAL y ningún estado suyo frena el formulario: sin él, o con
   * `no_disponible` (el informe todavía no se aceptó), la pestaña se dibuja
   * sin ninguno de los paneles de terreno; con `error` los paneles lo dicen con
   * calma y el formulario sigue guardando igual.
   */
  terreno?: EstadoDelTerreno;
  /** Abre la pestaña "Registro de terreno" (la misma navegación que la barra). */
  onVerTerreno?: () => void;
  etiquetaEnvio?: string;
  onCancelar?: () => void;
  onEnviar: (
    valores: InformeValues,
    input: CrearInformeInput,
    utilidades: {
      errorDeCampo: (campo: string, mensaje: string) => void;
      /**
       * Se cambió el encargado de un informe que estaba en la app de terreno
       * y se eligió enviarle la guía al nuevo AL GUARDAR. Lo hace la pantalla
       * después de que el guardado sale bien: el formulario no envía nada.
       */
      enviarGuiaAlGuardar: boolean;
    },
  ) => void;
}

/**
 * Formulario de creación del informe (RF-E1, RF-E4).
 *
 * Fuera de alcance a propósito, y NO por olvido:
 * - "Revisar historial de tasaciones" (RF-E3) consulta la base histórica de
 *   Consultek, que es otro módulo todavía no construido. El select de "tasación
 *   anterior" SÍ está, porque su dato (`es_retasacion`) ya se persiste y el
 *   usuario puede responderlo a mano.
 */
export function InformeFormulario({
  catalogos,
  enviando,
  bloqueado = false,
  ref,
  onCambios,
  valoresIniciales,
  informeId,
  informe,
  imagenPendiente = null,
  onImagenPendiente,
  importador,
  encabezado,
  terreno,
  onVerTerreno,
  modo = "crear",
  etiquetaEnvio = "Crear informe",
  onCancelar,
  onEnviar,
}: InformeFormularioProps) {
  const esEdicion = modo === "editar";

  const form = useForm<InformeValues>({
    resolver: zodResolver(schemaConRolesUnicos),
    defaultValues: valoresIniciales ?? VALORES_INICIALES,
    mode: "onSubmit",
  });

  /* `formState.isDirty` compara contra los `defaultValues`, así que vuelve a
     false solo si el usuario deshace a mano o si `reset` redefine la base. Eso
     es justo lo que se quiere: es la definición de "cambios sin guardar". */
  const { isDirty } = form.formState;

  /* --- Plantilla y contenidos ------------------------------------------------
     Elegir plantilla trae SUS ítems (`GET /plantillas-informe/{id}/contenidos`)
     y la selección arranca en sus obligatorios. Dos refs llevan el "qué falta
     hacer cuando lleguen los datos": la plantilla que acaba de elegirse y espera
     su selección inicial, y la última que ya se dejó consistente (la primera
     carga de una edición NO reinicia lo que el informe ya tenía). */
  const plantillaElegida = useWatch({ control: form.control, name: "plantilla_id" });
  const itemsElegidos = useWatch({ control: form.control, name: "items" });
  const idDePlantilla = plantillaElegida ? Number(plantillaElegida) : null;
  const nombreDeLaPlantilla =
    catalogos.plantillas.find((p) => p.id === idDePlantilla)?.nombre ?? null;
  const contenidosDeLaPlantilla = useContenidosDeLaPlantilla(idDePlantilla);
  const pendienteDeInicio = useRef<number | null>(null);
  const consistentePara = useRef<number | null>(null);
  const [cambioDePlantilla, setCambioDePlantilla] = useState<string | null>(null);

  const datosDeLaPlantilla = contenidosDeLaPlantilla.data;
  useEffect(() => {
    if (!datosDeLaPlantilla || idDePlantilla === null) return;

    if (pendienteDeInicio.current === idDePlantilla) {
      pendienteDeInicio.current = null;
      consistentePara.current = idDePlantilla;
      form.setValue("items", seleccionInicial(datosDeLaPlantilla), {
        shouldDirty: true,
        shouldValidate: form.formState.isSubmitted,
      });
    } else if (consistentePara.current !== idDePlantilla) {
      consistentePara.current = idDePlantilla;
      const actual = form.getValues("items");
      const normal = normalizarSeleccion(datosDeLaPlantilla, actual);
      if (normal.length !== actual.length || normal.some((id, i) => id !== actual[i])) {
        form.setValue("items", normal, { shouldDirty: false });
      }
    }
  }, [datosDeLaPlantilla, idDePlantilla, form]);

  function aplicarPlantilla(id: string) {
    pendienteDeInicio.current = id ? Number(id) : null;
    consistentePara.current = null;
    form.setValue("plantilla_id", id, {
      shouldDirty: true,
      shouldValidate: form.formState.isSubmitted,
    });
    form.setValue("items", [], { shouldDirty: true });
    // El formato de la plantilla anterior casi nunca vale para la nueva: se
    // propone el de por defecto de la nueva (el primero si son dos) y, si no
    // tiene ninguno, queda sin elegir.
    const formatos = catalogos.plantillas.find((p) => String(p.id) === id)?.formatos ?? [];
    const porDefecto = formatos.find((f) => f.por_defecto) ?? null;
    form.setValue("formato_salida_id", porDefecto ? String(porDefecto.id) : "", {
      shouldDirty: true,
      shouldValidate: form.formState.isSubmitted,
    });
    setCambioDePlantilla(null);
  }

  function elegirPlantilla(id: string) {
    if (id === form.getValues("plantilla_id")) return;
    // Reiniciar la selección tira una elección hecha a mano: se confirma.
    const tocada =
      datosDeLaPlantilla !== undefined &&
      form.getValues("plantilla_id") !== "" &&
      seleccionCambiada(datosDeLaPlantilla, form.getValues("items"));

    if (tocada) setCambioDePlantilla(id);
    else aplicarPlantilla(id);
  }

  useEffect(() => {
    onCambios?.(isDirty);
  }, [isDirty, onCambios]);

  useEffect(
    // Al desmontarse ya no hay nada que perder acá: si no se retracta, la
    // pantalla seguiría interrumpiendo salidas por un formulario que no existe.
    () => () => onCambios?.(false),
    [onCambios],
  );

  /* Sin arreglo de dependencias: el handle se reasigna en cada render a
     propósito. `enviar` se redefine con cada render y captura el `onEnviar` de
     ese render; congelarlo sobre `[form]` dejaría el diálogo llamando al
     callback de la primera pasada. */
  useImperativeHandle(ref, () => ({
    enviar: (alNoValidar) => void form.handleSubmit(enviar, () => alNoValidar?.())(),
    marcarGuardado: (valores) => form.reset(valores ?? form.getValues()),
    precargar: (propuesta) => {
      const actual = form.getValues();

      form.reset(
        {
          ...actual,
          /* El cliente (empresa, solicitante y correo) NO se precarga: el del
             informe nuevo no es necesariamente el de la tasación anterior y lo
             escribe la persona. */
          propiedad_nombre: propuesta.propiedad_nombre ?? actual.propiedad_nombre,
          direccion: propuesta.direccion ?? actual.direccion,
          descripcion_ubicacion: propuesta.descripcion_ubicacion ?? actual.descripcion_ubicacion,
          descripcion: propuesta.descripcion ?? actual.descripcion,
          /* LA UNIDAD VIAJA CON LOS ROLES Y NO SE PUEDE SEPARAR DE ELLOS. Las
             superficies que se precargan abajo son números sin unidad: son
             10,79 en la unidad que el DOCUMENTO usaba. Precargar las filas
             dejando el selector donde estaba escribe cada superficie
             correctamente y significando otra cosa —10,79 m² donde el papel
             decía 10,79 ha, un error de cuatro órdenes de magnitud— y no hay
             nada en la pantalla que lo delate, porque el número es el que el
             documento traía.

             El `??` conserva la unidad actual cuando el documento no la
             declara, igual que en el resto: sin evidencia no se pisa una
             elección que la persona ya pudo haber hecho. */
          unidad_superficie: propuesta.unidad_superficie ?? actual.unidad_superficie,
          roles:
            propuesta.roles.length > 0
              ? propuesta.roles.map((rol) => ({
                  rol_sii: rol.rol_sii,
                  comuna_codigo: rol.comuna_codigo ?? "",
                  comuna: rol.comuna ?? "",
                  superficie: rol.superficie === null ? "" : String(rol.superficie),
                  superficie_construida:
                    rol.superficie_construida === null ? "" : String(rol.superficie_construida),
                }))
              : actual.roles,
          /* El tipo de tasación (la plantilla) y el formato de salida no se
             proponen: los elige la persona. */
        },
        // `keepDefaultValues` es lo que deja el formulario SUCIO: sin él,
        // `reset` adoptaría lo precargado como la base nueva y `isDirty`
        // volvería a false — la guardia de salida dejaría irse sin avisar a
        // quien acaba de importar un documento entero.
        { keepDefaultValues: true },
      );
    },
    marcarTasacionAnterior: () => form.setValue("es_retasacion", "si", { shouldDirty: true }),
  }));

  const {
    fields: roles,
    append: agregarRol,
    remove: quitarRol,
  } = useFieldArray({ control: form.control, name: "roles" });

  /* `useWatch` y no `form.watch()`: re-renderiza solo este cálculo cuando
     cambian las superficies o la unidad, en vez de todo el formulario en cada
     tecla. */
  const rolesObservados = useWatch({ control: form.control, name: "roles" });
  const unidad = useWatch({ control: form.control, name: "unidad_superficie" }) ?? "ha";

  const sumaDeRoles = useMemo(
    () => sumarSuperficies((rolesObservados ?? []).map((rol) => rol?.superficie)),
    [rolesObservados],
  );

  /* El select de revisores ofrece los perfiles Revisor y Super Admin (RF-E1
     §8.1 + RF-G3); los otros dos ofrecen a cualquiera con acceso. */
  const revisores = useMemo(
    () =>
      catalogos.usuarios.filter(
        (usuario) =>
          usuario.perfil_codigo !== null && CODIGOS_REVISOR.includes(usuario.perfil_codigo),
      ),
    [catalogos.usuarios],
  );

  /* Hay números escritos en la ubicación que TODAVÍA no forman una coordenada.
     No puede salir del esquema de zod: el campo del formulario es el par ya
     armado, y lo que quedó a medias no llega hasta acá — por eso lo cuenta el
     selector, que es el único que ve los dos textos. */
  const [coordenadaIncompleta, setCoordenadaIncompleta] = useState(false);

  /* A quién tiene HOY el informe en su app de terreno, solo en la edición.
     Cambiar el encargado de un informe así pasa por una advertencia (decisión
     de Rodgers, 24-09-2026): el cambio lo saca del teléfono del saliente, y
     hay que decidir si se le envía la guía al nuevo ahora o después. */
  const enLaAppDe = modo === "editar" && informe?.terreno?.disponible ? informe.terreno.habilitado_para : null;
  const [cambioDeEncargado, setCambioDeEncargado] = useState<{
    valor: string;
    aplicar: (valor: string) => void;
  } | null>(null);
  const idDelFormulario = useId();
  const enviarGuiaAlGuardar = useRef(false);

  const opcionesPorRol: Record<RolResponsable, typeof catalogos.usuarios> = {
    encargado_terreno: catalogos.usuarios,
    ejecutivo: catalogos.usuarios,
    revisor_cotizacion: revisores,
    revisor_informe: revisores,
  };

  function enviar(valores: InformeValues) {
    /* Una coordenada a medio escribir NO es un predio sin ubicar. El selector
       emite `null` para las dos cosas —es lo único que puede guardar—, así que
       sin esta guardia el informe se enviaría como "sin ubicación" sobre dos
       campos que la persona ve escritos, y en verde. Mismo trato que reciben
       las fechas a medio teclear del formulario de cotización. */
    if (coordenadaIncompleta) {
      form.setError("coordenada", {
        type: "manual",
        message: "Completa la latitud y la longitud, o vacía las dos para dejar el predio sin ubicar.",
      });

      return;
    }

    onEnviar(
      valores,
      {
        // Sin `|| null`: el campo es obligatorio y zod ya rechazó el vacío
        // antes de llegar acá, así que acá siempre hay un número escrito.
        numero_informe: valores.numero_informe,
        cliente_nombre: valores.cliente_nombre,
        cargo_cliente: valores.cargo_cliente || null,
        telefono_contacto: valores.telefono_contacto,
        email_contacto: valores.email_contacto,
        solicitante_nombre: valores.solicitante_nombre,
        propiedad_nombre: valores.propiedad_nombre,
        // Los opcionales viajan como `null` y no como "": la columna es
        // nullable y un string vacío guardaría "sin dato" como si fuera un dato.
        direccion: valores.direccion || null,
        // `superficie_ha` NO viaja: la deriva el servidor sumando los roles.
        // Las dos mitades se desarman recién acá. Que salgan siempre juntas
        // —o las dos en `null`— es lo que el tipo de arriba garantiza.
        latitud: valores.coordenada?.latitud ?? null,
        longitud: valores.coordenada?.longitud ?? null,
        descripcion_ubicacion: valores.descripcion_ubicacion || null,
        descripcion: valores.descripcion || null,
        formato_salida_id: Number(valores.formato_salida_id),
        // Las TRES respuestas de RF-E3 viajan distintas: "" (sin verificar) va
        // como `null`, que la base distingue de `false` ("se consultó y no hubo
        // coincidencias").
        es_retasacion: valores.es_retasacion === "" ? null : valores.es_retasacion === "si",
        unidad_superficie: valores.unidad_superficie,
        nota_imagen: valores.nota_imagen || null,
        roles: valores.roles.map((rol) => ({
          // Ausente en un rol nuevo, y `JSON.stringify` no serializa
          // `undefined`: la clave directamente no viaja, que es lo que el alta
          // necesita — ahí el servidor la rechaza con un 422.
          id: rol.id,
          rol_sii: rol.rol_sii,
          comuna_codigo: rol.comuna_codigo || null,
          // NUNCA viaja `comuna`: el servidor la resuelve desde `comuna_codigo`
          // contra el maestro, y una copia mandada acá sería ignorada en el
          // mejor de los casos.
          superficie: parsearSuperficie(rol.superficie),
          superficie_construida: parsearSuperficie(rol.superficie_construida),
        })),
        plantilla_id: Number(valores.plantilla_id),
        // Normalizada contra la plantilla: con sus obligatorios y en su orden.
        items: contenidosDeLaPlantilla.data
          ? itemsParaEnviar(contenidosDeLaPlantilla.data, valores.items)
          : valores.items,
        responsables: Object.fromEntries(
          ROLES_RESPONSABLE.map(({ rol }) => [rol, Number(valores.responsables[rol])]),
        ) as Record<RolResponsable, number>,
      },
      {
        errorDeCampo: (campo, mensaje) =>
          form.setError(campoDelFormulario(campo), { type: "server", message: mensaje }),
        enviarGuiaAlGuardar: enviarGuiaAlGuardar.current,
      },
    );
  }

  const contenidosSeleccionados = itemsElegidos;
  const etiquetaUnidad = unidad === "ha" ? "ha" : "m²";
  const coordenada = useWatch({ control: form.control, name: "coordenada" });

  /* --- Lo que mandó terreno (solo la edición, y solo si el registro existe) ---
     `terrenoActivo` es `undefined` cuando no hay nada que dibujar: el alta, o un
     informe que todavía no se aceptó. */
  const terrenoActivo =
    esEdicion && informe && terreno && terreno.estado !== "no_disponible" ? terreno : undefined;
  const vista = useMemo(
    () => (terrenoActivo?.estado === "ok" ? vistaDeTerreno(terrenoActivo.registro) : null),
    [terrenoActivo],
  );
  const puntoDeAcceso = vista?.puntoDeAcceso ?? null;
  /* Lo que el encargado propuso sobre los roles: solo con registro de terreno. */
  const propuestasDeRoles = useMemo(
    () => rolesDeTerreno(vista?.seccion ?? null, vista?.encargado ?? null),
    [vista],
  );
  const puntoEnUso = puntoDeAcceso !== null && mismaCoordenada(coordenada, puntoDeAcceso);
  const fotosDelRegistro = useMemo(
    () => (vista?.seccion ? fotosDelVisor([vista.seccion], "todo") : []),
    [vista],
  );
  const [fotoAbierta, setFotoAbierta] = useState<string | null>(null);
  /* La coordenada que tenía el formulario ANTES de aplicar el punto de terreno.
     `{ valor }` y no el valor suelto: "no había ninguna" (`null`) es una
     respuesta, y se distingue de "no se aplicó nada" (el estado es `null`). */
  const [anteriorAlAplicar, setAnteriorAlAplicar] = useState<{
    valor: InformeValues["coordenada"];
  } | null>(null);
  const asignado = informe?.terreno?.habilitado_para?.nombre ?? null;

  /* A dónde vuelve "Volver a la anterior": a lo que había antes de aplicar y,
     si el punto se aplicó en otra sesión, a lo último guardado. Solo se ofrece
     si ese destino es DISTINTO del punto de terreno: volver a lo mismo sería un
     botón que parece hacer algo y no cambia nada. */
  const destinoDeVolver =
    anteriorAlAplicar !== null
      ? anteriorAlAplicar.valor
      : ((form.formState.defaultValues?.coordenada as InformeValues["coordenada"] | undefined) ??
        null);
  const puedeVolver =
    puntoDeAcceso !== null && (destinoDeVolver === null || !mismaCoordenada(destinoDeVolver, puntoDeAcceso));

  /* Aplicar el punto CAMBIA EL FORMULARIO y nada más: queda sucio como si se
     hubiera tecleado, y se guarda con "Guardar cambios". Se redondea a seis
     decimales, igual que el clic del mapa, para que lo que se guarda sea lo que
     el campo muestra. */
  function usarPuntoDeTerreno() {
    if (!puntoDeAcceso) return;

    setAnteriorAlAplicar({ valor: form.getValues("coordenada") });
    form.setValue("coordenada", coordenadaDelPunto(puntoDeAcceso), { shouldDirty: true });
    form.clearErrors("coordenada");
    setCoordenadaIncompleta(false);
  }

  function volverALaAnterior() {
    form.setValue("coordenada", destinoDeVolver, { shouldDirty: true });
    setAnteriorAlAplicar(null);
  }

  /* El aviso del punto de terreno sobre el mapa, con sus tres caras. Con el
     registro todavía cargando, o caído, no se dice nada: es un dato más que no
     llegó, no una advertencia. */
  const avisoDelPunto =
    terrenoActivo?.estado === "ok" && vista ? (
      !vista.sincronizado ? (
        <PuntoDeAccesoDeTerreno estado="pendiente" />
      ) : puntoDeAcceso ? (
        puntoEnUso ? (
          <PuntoDeAccesoDeTerreno
            estado="en_uso"
            punto={puntoDeAcceso}
            onVolver={puedeVolver ? volverALaAnterior : undefined}
          />
        ) : (
          <PuntoDeAccesoDeTerreno
            estado="disponible"
            punto={puntoDeAcceso}
            distancia={
              coordenada ? textoDeDistancia(distanciaEnMetros(coordenada, puntoDeAcceso)) : null
            }
            onUsar={usarPuntoDeTerreno}
          />
        )
      ) : null
    ) : null;

  /* "Redactar desde terreno" PROPONE: el texto entra al formulario como un
     cambio sin guardar y la casilla "Información validada" se desmarca, porque
     lo escribió un modelo y una persona tiene que revisarlo. Solo se ofrece con
     material que redactar (un texto o una transcripción lista): son los casos
     en que el servidor no respondería 422. */
  const botonDeRedaccion = (
    nombre: "descripcion_ubicacion" | "descripcion",
    campo: CampoDeTerreno | null,
  ) =>
    esEdicion &&
    informe &&
    terrenoActivo?.estado === "ok" &&
    puedeRedactarDesdeTerreno(campo) ? (
      <RedactarDesdeTerreno
        informeId={informe.id}
        campo={nombre}
        valorActual={form.watch(nombre) ?? ""}
        onRedactado={(texto) => {
          form.setValue(nombre, texto, { shouldDirty: true, shouldValidate: true });
          form.setValue(`${nombre}_validada`, false, { shouldDirty: true });
        }}
      />
    ) : null;

  const panelDeTerreno = (campo: CampoDeTerreno | null) =>
    terrenoActivo && informe ? (
      <PanelDeTerrenoDeLaDescripcion
        informeId={informe.id}
        terreno={terrenoActivo}
        vista={vista}
        campo={campo}
        asignado={asignado}
        onVerTodo={onVerTerreno}
        onAbrirFoto={setFotoAbierta}
      />
    ) : undefined;

  /* Vuelve el formulario a lo último guardado. `reset()` sin argumentos toma los
     `defaultValues` vigentes —la base que dejó `marcarGuardado`—, así que es
     exactamente "descartar lo que no se guardó". La coordenada a medio escribir
     NO se toca: lo que dice `coordenadaIncompleta` es lo que el selector tiene
     en pantalla, y el selector no se entera de este reset si el valor era
     `null` antes y después. */
  function descartarCambios() {
    form.reset();
    enviarGuiaAlGuardar.current = false;
  }

  const formulario = (
    /* `noValidate` apaga la validación NATIVA del navegador, que compite
       con zod y le gana: es la del agente de usuario y corre ANTES del
       submit. Con un `<input type="email">` mal escrito, el navegador
       cancela el envío y muestra su propio globo —en otro idioma, con otro
       estilo y fuera del DS— y `handleSubmit` no llega a correr, así que
       zod nunca opina y el `FormMessage` del campo queda vacío. El
       resultado es un formulario que rechaza sin decir por qué en la única
       forma en que el usuario sabe leerlo.
       Acá la validación la posee zod, entera: apagarla nativa deja UNA
       sola fuente de verdad y un solo lugar donde aparecen los errores.
       Es también lo que recomienda react-hook-form al usar un resolver. */
    <form
      id={idDelFormulario}
      noValidate
      onSubmit={form.handleSubmit(enviar)}
      className="min-w-0 space-y-4"
    >
      {/* Arriba de todo, y FUERA de las secciones de campos: es lo primero
          que conviene hacer con el formulario en blanco, y ponerlo abajo
          dejaría a alguien tecleando todo a mano para descubrir después que
          había una ayuda. Solo llega en la creación (ver la prop). */}
      {importador}

      {/* Lo que la pantalla quiere ver ANTES de la primera tarjeta —hoy los
          datos de facturación—. Solo en la edición: el alta no lo pasa. */}
      {esEdicion ? encabezado : null}

      {/* --- 01 · Identificación --- */}
      <TarjetaDeSeccion
        clave="ident"
        titulo="Identificación del informe"
        bajada="Quién encarga la tasación y sobre qué propiedad."
      >
        {/* El número del informe va PRIMERO y en su propia franja, no dentro de
            la grilla de dos columnas: es la identidad del documento —con lo
            que se cruza contra el ERP—, no un dato más del cliente. Angosto
            a propósito: un input a media pantalla para un código de una línea
            sugiere que se espera un texto largo. */}
        <FormField
          control={form.control}
          name="numero_informe"
          render={({ field }) => (
            <FormItem className="bg-success-soft/40 border-success-soft mb-5 rounded-lg border-[1.5px] p-4">
              <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
                <div className="w-full space-y-1.5 md:w-56 md:flex-none">
                  <div className="flex items-center gap-2">
                    <FormLabel>N° de informe</FormLabel>
                    <span className="bg-success-soft text-success-soft-foreground rounded-sm px-1.5 py-0.5 text-[0.59375rem] font-extrabold tracking-[0.05em] uppercase">
                      Clave ERP
                    </span>
                  </div>
                  <FormControl>
                    <Input
                      className="border-primary/35 font-mono font-bold"
                      placeholder="Ej: INF-2026-0147"
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormDescription className="min-w-48 flex-1 pt-1">
                  El número con el que este informe se registra en el ERP de Consultek. Es la
                  clave con la que se cruza contra ese sistema.
                </FormDescription>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-5 md:grid-cols-2">
          <FormField
            control={form.control}
            name="cliente_nombre"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre del cliente</FormLabel>
                <FormControl>
                  <Input autoComplete="off" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="cargo_cliente"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>Cargo del cliente</FormLabel>
                  <ChipOpcional />
                </div>
                <FormControl>
                  <Input
                    placeholder="Ej: Encarg. proyectos y tasaciones"
                    autoComplete="off"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Va bajo el nombre en la carta de la cotización. Si queda vacío, no se imprime.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="propiedad_nombre"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre de la propiedad</FormLabel>
                <FormControl>
                  <Input autoComplete="off" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="direccion"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Dirección de la propiedad</FormLabel>
                <FormControl>
                  <Input autoComplete="off" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </TarjetaDeSeccion>

      {/* --- 02 · Responsables y encuadre: los cuatro select van CONTIGUOS (RF-E1 §8.1) --- */}
      <TarjetaDeSeccion
        clave="resp"
        titulo="Responsables y encuadre"
        bajada="Se definen al arrancar el trabajo y casi no se vuelven a tocar."
      >
        <div className="grid gap-5 md:grid-cols-2">
          {ROLES_RESPONSABLE.map(({ rol, etiqueta, placeholder }) => (
            <FormField
              key={rol}
              control={form.control}
              name={`responsables.${rol}`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{etiqueta}</FormLabel>
                  <FormControl>
                    <NativeSelect
                      {...field}
                      onChange={(evento) => {
                        const valor = evento.target.value;

                        if (rol === "encargado_terreno" && enLaAppDe && valor !== String(enLaAppDe.id)) {
                          // El cambio queda EN ESPERA hasta que se decida:
                          // el select sigue mostrando al encargado de hoy.
                          setCambioDeEncargado({ valor, aplicar: field.onChange });
                          return;
                        }

                        // Volver al encargado de siempre deshace cualquier
                        // envío que se hubiera decidido para otro.
                        if (rol === "encargado_terreno") enviarGuiaAlGuardar.current = false;
                        field.onChange(evento);
                      }}
                    >
                      <NativeSelectOption value="">{placeholder}</NativeSelectOption>
                      {opcionesPorRol[rol].map((usuario) => (
                        <NativeSelectOption key={usuario.id} value={String(usuario.id)}>
                          {usuario.nombre}
                        </NativeSelectOption>
                      ))}
                    </NativeSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}

          <FormField
            control={form.control}
            name="plantilla_id"
            render={({ field }) => {
              const proposito = catalogos.plantillas.find(
                (p) => String(p.id) === field.value,
              )?.proposito;

              return (
                <FormItem>
                  <FormLabel>Tipo de tasación</FormLabel>
                  <FormControl>
                    <NativeSelect
                      value={field.value}
                      onBlur={field.onBlur}
                      onChange={(e) => elegirPlantilla(e.target.value)}
                    >
                      <NativeSelectOption value="">Selecciona el tipo de tasación</NativeSelectOption>
                      {catalogos.plantillas.map((plantilla) => (
                        <NativeSelectOption key={plantilla.id} value={String(plantilla.id)}>
                          {plantilla.nombre}
                        </NativeSelectOption>
                      ))}
                    </NativeSelect>
                  </FormControl>
                  {proposito ? <FormDescription>{proposito}</FormDescription> : null}
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="formato_salida_id"
            render={({ field }) => {
              /* Las opciones son los formatos que admite la plantilla elegida:
                 sin plantilla no hay qué ofrecer, y el select lo dice. */
              const formatos =
                catalogos.plantillas.find((p) => String(p.id) === plantillaElegida)?.formatos ?? [];
              const sinTipo = plantillaElegida === "";

              return (
                <FormItem>
                  <FormLabel>Formato de salida</FormLabel>
                  <FormControl>
                    <NativeSelect {...field} disabled={sinTipo}>
                      <NativeSelectOption value="">
                        {sinTipo ? "Selecciona primero el tipo de tasación" : "Selecciona el formato de salida"}
                      </NativeSelectOption>
                      {formatos.map((formato) => (
                        <NativeSelectOption key={formato.id} value={String(formato.id)}>
                          {formato.nombre}
                        </NativeSelectOption>
                      ))}
                    </NativeSelect>
                  </FormControl>
                  {sinTipo ? <FormDescription>Elige primero el tipo de tasación.</FormDescription> : null}
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>

        <div className="border-divisor mt-4 border-t pt-4">
          <FormField
            control={form.control}
            name="es_retasacion"
            render={({ field }) => (
              <FormItem>
                <FormLabel>¿La propiedad cuenta con tasación anterior?</FormLabel>
                <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
                  <FormControl>
                    <SegmentoDeRetasacion
                      value={field.value}
                      onChange={field.onChange}
                      className="w-[16.25rem] flex-none"
                    />
                  </FormControl>
                  {/* "Sin verificar" es un valor, no un placeholder: es
                      distinto de responder "No" y así se guarda. */}
                  <FormDescription className="min-w-48 flex-1 pt-1.5">
                    "Sin verificar" es un valor, no un vacío: dice que nadie consultó todavía. Se
                    imprime distinto a "no hay tasación previa".
                  </FormDescription>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </TarjetaDeSeccion>

      {/* --- 03 · La ubicación del predio -----------------------------------

          DOS CAMINOS AL MISMO DATO, porque son dos situaciones distintas:
          el mapa para el predio que se reconoce y no se sabe medir, y los
          campos para la coordenada que YA se tiene —de un GPS, de un
          plano, del visor del SII— y que hacer clic solo podría empeorar.
          Los dos viven dentro del selector, que es también el que garantiza
          que lo que llegue acá sea el par completo o nada. */}
      <TarjetaDeSeccion
        clave="ubic"
        titulo="Ubicación del predio"
        bajada="De esta coordenada salen las distancias a los comparables del informe."
        acciones={
          <EstadoDeLaCoordenada
            coordenada={coordenada}
            incompleta={coordenadaIncompleta}
            fuente={
              terrenoActivo?.estado === "ok" ? (puntoEnUso ? "terreno" : "cotizacion") : null
            }
          />
        }
      >
        {avisoDelPunto ? <div className="mb-3">{avisoDelPunto}</div> : null}

        <FormField
          control={form.control}
          name="coordenada"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="sr-only">Ubicación del predio</FormLabel>

              <Suspense fallback={<Skeleton className="h-72 w-full rounded-md" />}>
                <SelectorDeCoordenada
                  disposicion="lateral"
                  valor={field.value}
                  onCambio={field.onChange}
                  onCoordenadaIncompleta={(incompleta) => {
                    setCoordenadaIncompleta(incompleta);
                    /* Completarla retira el reclamo del envío anterior en
                       el acto. Esperar al próximo submit dejaría un campo
                       en rojo sobre dos números que ya están bien. */
                    if (!incompleta) form.clearErrors("coordenada");
                  }}
                  centroInicial={VISTA_INICIAL}
                  zoomInicial={ZOOM_INICIAL}
                />
              </Suspense>

              <FormMessage />
            </FormItem>
          )}
        />
      </TarjetaDeSeccion>

      {/* --- 04 · Roles del informe (RF-E1 §8.2) ---

          DOS NIVELES, no tres: cada fila de esta tabla ES un rol, con su
          comuna y sus dos superficies — la "tomaremos la idea de la tabla"
          del pedido original. Reversión deliberada del nivel "propiedad",
          que agrupaba varios roles bajo un mismo inmueble: nació de la
          22160, que en realidad eran CINCO tasaciones distintas metidas en
          un documento, y la 22192 (una propiedad, varios roles, una
          comuna) ya se representaba bien sin ese nivel. El caso real que
          esto tiene que soportar —una sucesión cuyos roles caen en
          comunas DISTINTAS— es exactamente lo que una fila por rol
          resuelve sin forzar nada. */}
      <TarjetaDeSeccion
        clave="roles"
        titulo="Roles del informe"
        bajada="Un Rol SII es único dentro de su comuna. La unidad es del informe entero."
        acciones={
          <div className="flex flex-wrap items-center gap-3">
            <FormField
              control={form.control}
              name="unidad_superficie"
              render={({ field }) => (
                <SegmentoDeUnidad value={field.value} onChange={field.onChange} />
              )}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => agregarRol(rolVacio())}
            >
              <PlusIcon className="size-3.5" strokeWidth={2.6} />
              Agregar rol
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* `table-fixed` con ancho solo en las columnas numéricas: la comuna se
              lleva el resto. Con anchos automáticos el navegador repartía parejo
              y una comuna larga no cabía en su selector. */}
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead variant="versalitas" className="w-32">
                  Rol SII
                </TableHead>
                <TableHead variant="versalitas">Comuna</TableHead>
                <TableHead variant="versalitas" className="w-32">
                  Sup. terreno ({etiquetaUnidad})
                </TableHead>
                <TableHead variant="versalitas" className="w-32">
                  Sup. construida (m²)
                </TableHead>
                {/* El texto oculto va ADENTRO: un `th` con `sr-only` sale del flujo y
                    su columna perdería el ancho fijo. */}
                <TableHead className="w-12">
                  <span className="sr-only">Acciones</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((campo, indice) => {
                /* `campo.id` NO es el id del rol: useFieldArray lo pisa con su
                   propia llave. El del rol vive en el valor del formulario. */
                const idDelRol = rolesObservados?.[indice]?.id;
                const propuesta =
                  idDelRol !== undefined ? propuestasDeRoles.propuestas.get(idDelRol) : undefined;

                return (
                  <Fragment key={campo.id}>
                    <FilaDeRol
                      control={form.control}
                      form={form}
                      indice={indice}
                      total={roles.length}
                      unidad={unidad}
                      onQuitar={() => quitarRol(indice)}
                    />
                    {propuesta ? (
                      <LineaDeRolDeTerreno
                        form={form}
                        indice={indice}
                        propuesta={propuesta}
                        unidad={unidad}
                      />
                    ) : null}
                  </Fragment>
                );
              })}

              {/* La fila TOTAL: la etiqueta cubre Rol + Comuna, la suma va
                  SOLO en la columna Superficie, y Superficie construida
                  queda EN BLANCO — su vacío en cada fila significa "s/i"
                  (existe y nadie la midió), y sumar eso afirmaría una
                  medición que nadie tomó. Solo Superficie puede sumarse,
                  porque su vacío significa "—" (no aplica). Sin ningún rol
                  con superficie cargada se muestra "—", nunca "0". */}
              <TableRow className="bg-muted/40 hover:bg-muted/40 font-bold">
                <TableCell
                  colSpan={2}
                  className="text-ink-body text-[0.65625rem] font-extrabold tracking-[0.06em] uppercase"
                >
                  Total terreno · {roles.length} {roles.length === 1 ? "rol" : "roles"}
                </TableCell>
                <TableCell className="font-mono">
                  {sumaDeRoles > 0 ? `${formatearSuperficie(sumaDeRoles)} ${etiquetaUnidad}` : "—"}
                </TableCell>
                <TableCell className="text-ink-ghost text-[0.6875rem] font-semibold">
                  no se suma
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>

          {/* Los roles que el encargado creó en el predio y la tabla no tiene. */}
          <RolesAgregadosEnTerreno
            nuevos={propuestasDeRoles.nuevos}
            filas={rolesObservados ?? []}
            unidad={unidad}
            onAgregar={(valores) => agregarRol(valores)}
          />

          <p className="text-ink-muted flex items-start gap-2 text-xs font-semibold text-pretty">
            <InfoIcon className="text-ink-hint mt-px size-3.5 flex-none" aria-hidden="true" />
            <span>
              El vacío significa distinto en cada columna: en terreno es{" "}
              <strong className="text-ink">no aplica</strong>; en construida es{" "}
              <strong className="text-ink">sin información</strong>. Por eso solo terreno lleva
              total.
            </span>
          </p>
        </div>
      </TarjetaDeSeccion>

      {/* --- 05 · Imagen satelital: ÚNICA por informe, ya no una por bloque ---
          Reversión del mismo motivo que los roles: el documento real
          lleva una captura por CONJUNTO, pero agruparlas en "bloques" era
          maquinaria para el caso incorrecto de la 22160. Con un solo
          predio por informe (el caso corriente), una imagen alcanza; el
          caso de roles en comunas distintas se explica con la nota. */}
      <TarjetaDeSeccion
        clave="imagenes"
        titulo="Imágenes del informe"
        bajada={
          terrenoActivo?.estado === "ok"
            ? "Imagen satelital y fotografía de portada. Ambas se imprimen en el informe."
            : "La imagen satelital del predio, con la nota que se imprime junto a ella."
        }
        acciones={
          esEdicion ? (
            <span className="bg-muted text-ink-muted rounded-sm px-2 py-1 text-[0.65625rem] font-bold">
              Se guarda al soltarla
            </span>
          ) : null
        }
      >
        <div>
          <h3 className="text-ink text-[0.8125rem] font-bold">Imagen satelital</h3>
          <p className="text-ink-body mt-0.5 text-xs font-semibold">
            Referencial, con el polígono según cartografía SII.
          </p>
          <div className="mt-2.5 grid items-start gap-4 md:grid-cols-[minmax(0,21.25rem)_minmax(0,1fr)]">
            <div>
              {esEdicion && informe ? (
                <ImagenDeLaPropiedad informeId={informe.id} imagen={informe.imagen ?? null} />
              ) : (
                <ImagenPendiente
                  archivo={imagenPendiente}
                  onArchivo={(a) => onImagenPendiente?.(a)}
                />
              )}
            </div>

            <FormField
              control={form.control}
              name="nota_imagen"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel htmlFor="nota-imagen">Nota de la imagen</FormLabel>
                    <ChipOpcional />
                  </div>
                  <FormControl>
                    {/* Con asistencia de redacción, igual que las dos
                        descripciones de más abajo y por el mismo motivo: es
                        prosa que se IMPRIME en el documento del cliente, al
                        lado de la imagen. */}
                    <CampoConAsistencia
                      id="nota-imagen"
                      campo={CAMPOS_CON_ASISTENCIA.notaImagen}
                      value={field.value}
                      onChange={field.onChange}
                      informeId={informeId}
                      rows={4}
                      placeholder="Aclara lo que la imagen no muestra. Sale junto a ella en el documento."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* La portada llega de la App de terreno o se sube a mano (gana la
            manual). Antes de aceptar el servidor no admite encuadres (422) y
            el registro de terreno no existe: no se dibuja. */}
        {terrenoActivo?.estado === "ok" && vista && informe ? (
          <PortadaDelInforme
            informeId={informe.id}
            portada={vista.portada}
            manual={informe.imagenes?.portada ?? null}
            encargado={vista.encargado ?? asignado}
            onAbrir={setFotoAbierta}
          />
        ) : null}
      </TarjetaDeSeccion>

      {/* --- 06 · Contacto y superficie declarada --- */}
      <TarjetaDeSeccion
        clave="contacto"
        titulo="Contacto y superficie declarada"
        bajada="A este correo se envía la cotización cuando el revisor la visa."
      >
        <div className="grid gap-5 md:grid-cols-2">
          <FormField
            control={form.control}
            name="solicitante_nombre"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre del solicitante</FormLabel>
                <FormControl>
                  <Input autoComplete="off" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* El correo va pegado al teléfono y no al final: los dos son la vía
              de contacto del cliente, y separarlos deja al usuario buscando
              dónde se pide el correo. */}
          <FormField
            control={form.control}
            name="email_contacto"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Correo del cliente</FormLabel>
                <FormControl>
                  <Input type="email" inputMode="email" autoComplete="off" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="telefono_contacto"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Teléfono del cliente</FormLabel>
                <FormControl>
                  <Input autoComplete="off" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* SOLO LECTURA: es la suma de la superficie de los roles y nadie
              la escribe. Va en la unidad del informe, no en "há" fijas: con
              el cuadro en m2 el rótulo anterior mentía. Sin ninguna
              superficie cargada imprime "—" y nunca "0". Es un campo suelto
              y no un `FormField`: no es un valor del formulario, así que tampoco usa
              `FormLabel`, que exige ese contexto. */}
          <div className="grid gap-2">
            <Label htmlFor="superficie-propiedad">Superficie de la propiedad ({etiquetaUnidad})</Label>
            <Input
              id="superficie-propiedad"
              readOnly
              value={
                sumaDeRoles > 0 ? `${formatearSuperficie(sumaDeRoles)} ${etiquetaUnidad}` : "—"
              }
            />
            <p className="text-muted-foreground text-sm">
              Se calcula sumando la superficie de cada rol.
            </p>
          </div>
        </div>
      </TarjetaDeSeccion>

      {/* --- 07 · Descripciones (RF-E2) ---
          Los dos campos con asistencia de redacción. La casilla
          "Información validada" NO la reemplaza: dice "yo revisé esto",
          mientras que el punto verde de la asistencia dice "esto pasó
          por IA y lo acepté". Son dos hechos distintos sobre el mismo
          campo y el usuario puede querer uno sin el otro. */}
      <TarjetaDeSeccion
        clave="desc"
        titulo="Descripciones"
        bajada={
          terrenoActivo
            ? "Texto que se imprime tal cual. A la derecha, lo que envió el encargado de terreno para interpretarlo y redactar."
            : "Texto que se imprime tal cual."
        }
      >
        <div className="space-y-6">
          <FilaDeDescripcion panel={panelDeTerreno(vista?.descripcion_ubicacion ?? null)}>
            <FormField
              control={form.control}
              name="descripcion_ubicacion"
              render={({ field }) => (
                <FormItem className="flex h-full flex-col gap-2">
                  <FormLabel htmlFor="descripcion-ubicacion">
                    Descripción de la ubicación de la propiedad
                  </FormLabel>
                  <FormControl>
                    <CampoConAsistencia
                      id="descripcion-ubicacion"
                      campo={CAMPOS_CON_ASISTENCIA.descripcionUbicacion}
                      value={field.value}
                      onChange={field.onChange}
                      informeId={informeId}
                      rows={8}
                      llenarAlto
                      variantDeMejora="outline"
                      accionesAntes={botonDeRedaccion(
                        "descripcion_ubicacion",
                        vista?.descripcion_ubicacion ?? null,
                      )}
                      accionesDespues={
                        esEdicion ? (
                          <CasillaDeValidacion
                            control={form.control}
                            name="descripcion_ubicacion_validada"
                          />
                        ) : null
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FilaDeDescripcion>

          <FilaDeDescripcion panel={panelDeTerreno(vista?.descripcion ?? null)}>
            <FormField
              control={form.control}
              name="descripcion"
              render={({ field }) => (
                <FormItem className="flex h-full flex-col gap-2">
                  <FormLabel htmlFor="descripcion-propiedad">Descripción de la propiedad</FormLabel>
                  <FormControl>
                    <CampoConAsistencia
                      id="descripcion-propiedad"
                      campo={CAMPOS_CON_ASISTENCIA.descripcion}
                      value={field.value}
                      onChange={field.onChange}
                      informeId={informeId}
                      rows={8}
                      llenarAlto
                      variantDeMejora="outline"
                      accionesAntes={botonDeRedaccion("descripcion", vista?.descripcion ?? null)}
                      accionesDespues={
                        esEdicion ? (
                          <CasillaDeValidacion control={form.control} name="descripcion_validada" />
                        ) : null
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FilaDeDescripcion>
        </div>
      </TarjetaDeSeccion>

      {/* --- 08 · Contenidos del informe (RF-E4) --- */}
      <TarjetaDeSeccion
        clave="contenidos"
        titulo="Contenidos del informe"
        bajada="Qué contenidos lleva el informe. Salen del tipo de tasación elegido arriba; los obligatorios siempre van."
        conAviso={
          (plantillaElegida === "" || contenidosSeleccionados.length === 0) &&
          form.formState.isSubmitted
        }
        acciones={
          datosDeLaPlantilla ? (
            <span
              className={cn(
                "rounded-sm px-2.5 py-1 text-[0.6875rem] font-bold",
                contenidosSeleccionados.length > 0
                  ? "bg-success-soft text-success-soft-foreground"
                  : "bg-danger-soft text-danger-soft-foreground",
              )}
            >
              {contenidosSeleccionados.length > 0
                ? `${contenidosSeleccionados.length} de ${datosDeLaPlantilla.length} seleccionados`
                : "Falta elegir al menos uno"}
            </span>
          ) : null
        }
      >
        {esEdicion && informe && !informe.plantilla && (informe.contenidos?.length ?? 0) > 0 ? (
          <div className="bg-info-soft text-info-soft-foreground mb-4 rounded-lg p-3 text-sm">
            <p className="font-bold">Este informe es anterior a las plantillas.</p>
            <p className="mt-0.5">
              Llevaba estos contenidos:{" "}
              {(informe.contenidos ?? []).map((contenido) => contenido.nombre).join(", ")}. Elige
              el tipo de tasación para poder guardar los cambios.
            </p>
          </div>
        ) : null}

        {catalogos.plantillas.length === 0 ? (
          <p className="text-ink-muted text-sm">
            No hay tipos de tasación (plantillas) definidos todavía. Un Super Admin los crea en
            Mantenedor de informes → Contenidos.
          </p>
        ) : (
          <div className="space-y-5">
            {nombreDeLaPlantilla ? (
              <p className="text-ink-muted text-sm">
                Contenidos del tipo de tasación «{nombreDeLaPlantilla}».
              </p>
            ) : null}

            {idDePlantilla === null ? (
              <p className="text-ink-muted text-sm">
                Elige el tipo de tasación para ver los contenidos que puede llevar el informe.
              </p>
            ) : contenidosDeLaPlantilla.isPending ? (
              <Skeleton className="h-40 w-full rounded-xl" />
            ) : contenidosDeLaPlantilla.isError ? (
              <p className="text-destructive text-sm">
                No pudimos cargar los contenidos de la plantilla. Vuelve a elegirla para reintentar.
              </p>
            ) : datosDeLaPlantilla && datosDeLaPlantilla.length === 0 ? (
              <p className="text-ink-muted text-sm">Esta plantilla todavía no tiene contenidos.</p>
            ) : datosDeLaPlantilla ? (
              <FormField
                control={form.control}
                name="items"
                render={({ field }) => (
                  <FormItem>
                    <SelectorDeContenidos
                      contenidos={datosDeLaPlantilla}
                      value={field.value}
                      onChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
          </div>
        )}
      </TarjetaDeSeccion>

      <AlertDialog
        open={cambioDePlantilla !== null}
        onOpenChange={(abierto) => !abierto && setCambioDePlantilla(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cambiar el tipo de tasación?</AlertDialogTitle>
            <AlertDialogDescription>
              Ya elegiste contenidos a mano. Al cambiar el tipo de tasación la selección vuelve a los
              contenidos obligatorios de la nueva.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Conservar la actual</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => cambioDePlantilla !== null && aplicarPlantilla(cambioDePlantilla)}
            >
              Cambiar el tipo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* --- 09 · Notas: solo la edición, el alta no las acepta todavía --- */}
      {esEdicion ? (
        <TarjetaDeSeccion
          clave="notas"
          titulo="Notas u observaciones"
          bajada="Uso interno del equipo. No se imprime en el informe."
        >
          <NotasDeLaPestanaDelForm control={form.control} />
        </TarjetaDeSeccion>
      ) : null}

      {esEdicion ? null : (
        <div className="flex items-center gap-4">
          <Button type="submit" size="lg" disabled={enviando || bloqueado}>
            {enviando ? <Spinner /> : null}
            {etiquetaEnvio}
          </Button>
          {onCancelar ? (
            <Button type="button" variant="outline" size="lg" onClick={onCancelar}>
              Cancelar
            </Button>
          ) : null}
          {/* Contador en mono + hint: es metadata, no compite con la acción. */}
          <span className="text-caption text-ink-hint">
            <span className="font-mono">{contenidosSeleccionados.length}</span> contenidos
            seleccionados
          </span>
        </div>
      )}
    </form>
  );

  return (
    <Form {...form}>
      {esEdicion ? (
        /* La columna de la derecha vive FUERA del `<form>` a propósito: es
           navegación, no campos, y sus botones no tienen por qué heredar el
           submit. */
        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_15rem]">
          {formulario}
          <IndiceDelFormulario
            control={form.control}
            coordenadaIncompleta={coordenadaIncompleta}
            hayImagen={Boolean(informe?.imagen)}
            puntoDeTerrenoSinAplicar={puntoDeAcceso !== null && !puntoEnUso}
          />
        </div>
      ) : (
        formulario
      )}

      {/* La barra de guardado vive FUERA del `<form>` y del grid de dos
          columnas para cruzar el ancho entero del panel principal (diseño v2:
          márgenes negativos), y no solo el de la columna del formulario. Su
          botón envía igual: apunta al `<form>` por id. */}
      {esEdicion ? (
        <BarraDelFormulario
          control={form.control}
          formId={idDelFormulario}
          enviando={enviando}
          bloqueado={bloqueado}
          etiquetaEnvio={etiquetaEnvio}
          onDescartar={descartarCambios}
        />
      ) : null}

      {/* UN solo visor para la portada y las fotos de las dos descripciones:
          recorre todas las de la sección, igual que en la pestaña de terreno. */}
      {terrenoActivo && informe ? (
        <VisorDeFotos
          informeId={informe.id}
          fotos={fotosDelRegistro}
          abierta={fotoAbierta}
          onCambiar={setFotoAbierta}
        />
      ) : null}

      <ModalDeCambioDeEncargado
        abierta={cambioDeEncargado !== null}
        saliente={enLaAppDe?.nombre ?? null}
        entrante={
          catalogos.usuarios.find((usuario) => String(usuario.id) === cambioDeEncargado?.valor)?.nombre ??
          null
        }
        onCancelar={() => setCambioDeEncargado(null)}
        onDecidir={(enviarAhora) => {
          enviarGuiaAlGuardar.current = enviarAhora;
          cambioDeEncargado?.aplicar(cambioDeEncargado.valor);
          setCambioDeEncargado(null);
        }}
      />
    </Form>
  );
}

/** Lleva la vista a la tarjeta de una sección. */
function irALaSeccion(clave: ClaveDeSeccion) {
  document.getElementById(idDeLaSeccion(clave))?.scrollIntoView?.({
    behavior: "smooth",
    block: "start",
  });
}

/**
 * El índice fijo y los pendientes, atados a los valores VIVOS del formulario.
 *
 * Es un componente aparte y no una cuenta en `InformeFormulario` para que cada
 * tecla re-renderice solo esto: `useWatch` sin nombre avisa de TODO cambio, y
 * en el formulario entero eso sería repintar las nueve tarjetas por letra.
 */
function IndiceDelFormulario({
  control,
  coordenadaIncompleta,
  hayImagen,
  puntoDeTerrenoSinAplicar = false,
}: {
  control: Control<InformeValues>;
  coordenadaIncompleta: boolean;
  hayImagen: boolean;
  puntoDeTerrenoSinAplicar?: boolean;
}) {
  const valores = useWatch({ control }) as Partial<InformeValues>;

  const entrada: EntradaDeAntecedentes = {
    numero_informe: valores.numero_informe ?? "",
    cliente_nombre: valores.cliente_nombre ?? "",
    propiedad_nombre: valores.propiedad_nombre ?? "",
    solicitante_nombre: valores.solicitante_nombre ?? "",
    telefono_contacto: valores.telefono_contacto ?? "",
    email_contacto: valores.email_contacto ?? "",
    responsables: { ...(valores.responsables ?? {}) } as Record<string, string>,
    coordenada: valores.coordenada ?? null,
    coordenadaIncompleta,
    roles: (valores.roles ?? []).map((rol) => ({ rol_sii: rol?.rol_sii ?? "" })),
    hayImagen,
    descripcion_ubicacion: valores.descripcion_ubicacion ?? "",
    descripcion: valores.descripcion ?? "",
    descripcion_ubicacion_validada: valores.descripcion_ubicacion_validada ?? false,
    descripcion_validada: valores.descripcion_validada ?? false,
    contenidos: valores.items?.length ?? 0,
    notas: valores.notas_observaciones ?? "",
    puntoDeTerrenoSinAplicar,
  };

  return (
    <IndiceDeLaPestana
      completas={seccionesCompletas(entrada)}
      pendientes={pendientesDeLaPestana(entrada)}
      onIr={irALaSeccion}
    />
  );
}

/** La barra de guardado, con su propia suscripción a los campos sucios. */
function BarraDelFormulario({
  control,
  ...resto
}: {
  control: Control<InformeValues>;
} & Omit<ComponentProps<typeof BarraDeGuardado>, "cambios">) {
  const { dirtyFields, isDirty } = useFormState({ control });

  /* `isDirty` es la verdad que usa la guardia de salida; `dirtyFields` es la
     cuenta fina. Si alguna vez discreparan —un campo sucio que RHF no
     desglosa—, la barra no puede decir "sin cambios" mientras la guardia
     bloquea la salida. */
  /* La coordenada va aparte: son dos hojas (latitud y longitud) pero UN
     cambio para quien la mueve, y "2 cambios sin guardar" tras un solo clic
     en el mapa se lee como un error. */
  const { coordenada, ...otrosSucios } = dirtyFields;
  const cambios = Math.max(contarCambios(otrosSucios) + (coordenada ? 1 : 0), isDirty ? 1 : 0);

  return <BarraDeGuardado cambios={cambios} {...resto} />;
}

/**
 * Una descripción con su casilla y, a la derecha, lo que lee de terreno.
 * Sin `panel` ocupa todo el ancho: es el alta, o un informe sin terreno.
 */
function FilaDeDescripcion({ children, panel }: { children: ReactNode; panel?: ReactNode }) {
  return (
    <div
      className={cn(
        "grid items-stretch gap-4",
        panel && "lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]",
      )}
    >
      <div className="flex min-w-0 flex-col">{children}</div>
      {panel}
    </div>
  );
}

/** El estado de la coordenada, que es lo que el índice de la tarjeta resume. */
function EstadoDeLaCoordenada({
  coordenada,
  incompleta,
  fuente,
}: {
  coordenada: InformeValues["coordenada"] | undefined;
  incompleta: boolean;
  /**
   * De dónde sale el punto, cuando se sabe: `terreno` si es el que confirmó la
   * visita, `cotizacion` si es cualquier otro. `null` cuando no hay registro
   * de terreno que pueda decirlo.
   */
  fuente: "terreno" | "cotizacion" | null;
}) {
  const completa = coordenada != null && !incompleta;

  return (
    <>
      {fuente ? (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-[0.6875rem] font-bold",
            fuente === "terreno"
              ? "bg-success-soft text-success-soft-foreground"
              : "bg-warning-soft text-warning-soft-foreground",
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              "size-[7px] rounded-full",
              fuente === "terreno" ? "bg-success-dot" : "bg-warning-dot",
            )}
          />
          {fuente === "terreno" ? "Fuente: visita en terreno" : "Fuente: cotización"}
        </span>
      ) : null}
      <span
        className={cn(
          "rounded-sm px-2.5 py-1 text-[0.6875rem] font-bold",
          completa
            ? "bg-success-soft text-success-soft-foreground"
            : incompleta
              ? "bg-danger-soft text-danger-soft-foreground"
              : "bg-muted text-ink-muted",
        )}
      >
        {completa ? "Coordenada completa" : incompleta ? "Coordenada incompleta" : "Sin marcar"}
      </span>
    </>
  );
}

/**
 * La validación de cada texto largo, como píldora (diseño v2): «Marcar como
 * revisada» y, ya marcada, «Revisada por una persona». Es POR CAMPO, no por
 * pestaña: el usuario puede dar por revisada la ubicación y no la
 * descripción, así que son dos banderas independientes.
 *
 * Es un `checkbox` accesible (el nombre cambia con el estado) y no un
 * `Checkbox` del DS: el diseño la dibuja como un botón con borde.
 */
function CasillaDeValidacion({
  control,
  name,
}: {
  control: Control<InformeValues>;
  name: "descripcion_ubicacion_validada" | "descripcion_validada";
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <button
            type="button"
            role="checkbox"
            aria-checked={field.value}
            onClick={() => field.onChange(!field.value)}
            className={cn(
              "focus-visible:ring-ring/50 inline-flex items-center gap-2 rounded-md border-[1.5px] px-2.5 py-1.5 text-[0.71875rem] font-bold outline-none focus-visible:ring-[3px]",
              field.value
                ? "border-success-soft bg-success-soft text-success-soft-foreground"
                : "border-input bg-card text-ink-body hover:border-primary/50",
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                "flex size-[0.9375rem] flex-none items-center justify-center rounded-[5px] border-[1.5px] text-white",
                field.value ? "border-primary bg-primary" : "border-input bg-card",
              )}
            >
              {field.value ? <CheckIcon className="size-2.5" strokeWidth={3.6} /> : null}
            </span>
            {field.value ? "Revisada por una persona" : "Marcar como revisada"}
          </button>
        </FormItem>
      )}
    />
  );
}

/** El chip gris «opcional» que acompaña a un rótulo, fuera de él (no en paréntesis). */
function ChipOpcional() {
  return <span className="text-ink-hint text-xs leading-none font-semibold">opcional</span>;
}

/** Notas al pie de la pestaña de generales, atadas al formulario. */
function NotasDeLaPestanaDelForm({ control }: { control: Control<InformeValues> }) {
  return (
    <FormField
      control={control}
      name="notas_observaciones"
      render={({ field }) => (
        <FormItem>
          <NotasDeLaPestana
            id="notas-generales"
            value={field.value}
            onChange={field.onChange}
            plana
          />
        </FormItem>
      )}
    />
  );
}
